// src/features/task/logic.ts
// Framework-agnostic business logic for Tasks.
// Zero React imports — pure TypeScript functions and data maps.

import type {
  Task,
  TaskStatusDefinition,
  TaskTypeDefinition,
  CustomFieldDefinition,
  CustomFieldValue,
  ChangeTaskStatusRequest,
  UpdateTaskRequest,
  TaskViewFilter,
} from '@core/types/task';
import type { Entity } from '@core/types/entity';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDate(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatDateTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatRelativeTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    if (isNaN(diff)) return formatDate(dateStr, locale);
    const sec = Math.max(0, Math.floor(diff / 1000));
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' });
    if (sec < 60) return rtf.format(-sec, 'second');
    const min = Math.floor(sec / 60);
    if (min < 60) return rtf.format(-min, 'minute');
    const hr = Math.floor(min / 60);
    if (hr < 24) return rtf.format(-hr, 'hour');
    const day = Math.floor(hr / 24);
    if (day < 7) return rtf.format(-day, 'day');
    if (day < 30) return rtf.format(-Math.floor(day / 7), 'week');
    return formatDate(dateStr, locale);
  } catch {
    return String(dateStr);
  }
}

export function formatDuration(durationStr: string | undefined | null): string {
  if (!durationStr) return '--';
  const match = String(durationStr).match(/^(\d+(?:\.\d+)?)s?$/);
  if (!match) return String(durationStr);
  const totalSeconds = parseFloat(match[1]);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.round(totalSeconds)}s`;
}

export function formatProgress(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

// ---------------------------------------------------------------------------
// Avatar / color helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  'hsl(239 84% 67%)',
  'hsl(330 81% 60%)',
  'hsl(38 92% 50%)',
  'hsl(160 84% 39%)',
  'hsl(217 91% 60%)',
  'hsl(258 90% 66%)',
  'hsl(0 84% 60%)',
  'hsl(174 84% 40%)',
];

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ---------------------------------------------------------------------------
// Entity ID extractor
// ---------------------------------------------------------------------------

export function getEntityId(item: any): string {
  return item?.entity?.id || item?.id || '';
}

export function getDisplayName(actor: any): string {
  return actor?.display_name || actor?.name || actor?.entity?.id?.substring(0, 8) || '--';
}

// ---------------------------------------------------------------------------
// Task data accessors (normalize nested task aspect data)
// ---------------------------------------------------------------------------

export function getTaskStatus(item: any): string {
  return item?.task?.status || item?.status || '';
}

export function getTaskPriority(item: any): string {
  return item?.task?.priority || item?.priority || '';
}

export function getTaskAssignee(item: any): any {
  return item?.task?.assignee || item?.assignee || null;
}

export function getTaskDueDate(item: any): string | undefined {
  return item?.task?.due_date || item?.due_date;
}

export function getTaskProgress(item: any): number {
  return item?.task?.progress_percent || 0;
}

export function getTaskType(item: any): string {
  return item?.type || '';
}

export function getTaskCustomStatus(item: any): string {
  return item?.task?.custom_status || '';
}

export function getTaskTags(item: any): string[] {
  return item?.task?.tags || [];
}

// ---------------------------------------------------------------------------
// Status → Badge variant maps (return i18n keys for labels)
// ---------------------------------------------------------------------------

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'primary' | 'secondary';

interface StatusMapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const taskStatusMap: Record<string, StatusMapping> = {
  TASK_STATUS_DRAFT: { variant: 'secondary', labelKey: 'task.status.draft' },
  TASK_STATUS_OPEN: { variant: 'primary', labelKey: 'task.status.open' },
  TASK_STATUS_IN_PROGRESS: { variant: 'warning', labelKey: 'task.status.inProgress' },
  TASK_STATUS_IN_REVIEW: { variant: 'warning', labelKey: 'task.status.inReview' },
  TASK_STATUS_BLOCKED: { variant: 'error', labelKey: 'task.status.blocked' },
  TASK_STATUS_DONE: { variant: 'success', labelKey: 'task.status.done' },
  TASK_STATUS_CANCELLED: { variant: 'error', labelKey: 'task.status.cancelled' },
  TASK_STATUS_ARCHIVED: { variant: 'secondary', labelKey: 'task.status.archived' },
};

export const taskPriorityMap: Record<string, StatusMapping> = {
  TASK_PRIORITY_HIGHEST: { variant: 'error', labelKey: 'task.priority.highest' },
  TASK_PRIORITY_HIGH: { variant: 'error', labelKey: 'task.priority.high' },
  TASK_PRIORITY_MEDIUM: { variant: 'warning', labelKey: 'task.priority.medium' },
  TASK_PRIORITY_LOW: { variant: 'primary', labelKey: 'task.priority.low' },
  TASK_PRIORITY_LOWEST: { variant: 'secondary', labelKey: 'task.priority.lowest' },
};

export const taskTypeMap: Record<string, { labelKey: string; iconPath: string }> = {
  TASK_TYPE_TASK: {
    labelKey: 'task.type.task',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  TASK_TYPE_BUG: {
    labelKey: 'task.type.bug',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  },
  TASK_TYPE_FEATURE: {
    labelKey: 'task.type.feature',
    iconPath: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  },
  TASK_TYPE_IMPROVEMENT: {
    labelKey: 'task.type.improvement',
    iconPath: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  },
  TASK_TYPE_SUPPORT: {
    labelKey: 'task.type.support',
    iconPath: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z',
  },
  TASK_TYPE_QUESTION: {
    labelKey: 'task.type.question',
    iconPath: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  TASK_TYPE_SUBTASK: {
    labelKey: 'task.type.subtask',
    iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16',
  },
  TASK_TYPE_MILESTONE: {
    labelKey: 'task.type.milestone',
    iconPath: 'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9',
  },
  TASK_TYPE_RECURRING: {
    labelKey: 'task.type.recurring',
    iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  },
};

export function getStatusMapping(
  map: Record<string, StatusMapping>,
  status: string | undefined,
): StatusMapping {
  if (!status) return { variant: 'secondary', labelKey: 'common.unknown' };
  return map[status] ?? { variant: 'secondary', labelKey: 'common.unknown' };
}

export function getTaskTypeInfo(type: string | undefined) {
  if (!type) return null;
  return taskTypeMap[type] ?? null;
}

// ---------------------------------------------------------------------------
// TaskTypeDefinition resolution
// ---------------------------------------------------------------------------

/**
 * Built-in TaskTypeDefinitions are registered with slug "core.{type}" where
 * {type} is the lowercased TaskType enum stem (see api/models/v1/task.proto:
 * `{ slug: "core.task", product: "core", display_name: "General Task" }`).
 * This is a formula, not a per-value lookup table — it stays correct as new
 * base TaskType enum values are added without touching this file.
 */
export function coreTaskTypeSlug(type: string): string {
  return `core.${type.replace('TASK_TYPE_', '').toLowerCase()}`;
}

/**
 * Resolve the TaskTypeDefinition backing a selected TaskType enum value from
 * the definitions actually loaded from the backend (ListTaskTypes), instead
 * of a hardcoded enum→slug table. Matches by the conventional core slug
 * first, then falls back to a direct definition-id match (selecting a custom
 * type by id). Returns null when nothing matches — callers should not assume
 * every base TaskType has a registered definition.
 */
export function findTaskTypeDefinition(
  definitions: TaskTypeDefinition[],
  type: string | undefined,
): TaskTypeDefinition | null {
  if (!type || definitions.length === 0) return null;
  const slug = coreTaskTypeSlug(type);
  return (
    definitions.find((d) => d.slug === slug) ??
    definitions.find((d) => d.entity?.id === type) ??
    null
  );
}

// ---------------------------------------------------------------------------
// Custom field value payload (typed per field_type — mirrors
// TaskCustomFieldsPanel's FieldInput onChange payloads so initial values sent
// on create match the shape SetTaskCustomField sends on edit).
// ---------------------------------------------------------------------------

export function buildCustomFieldValuePayload(
  def: CustomFieldDefinition,
  rawValue: string,
): CustomFieldValue | null {
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  switch (def.field_type) {
    case 'CUSTOM_FIELD_TYPE_NUMBER': {
      const n = parseFloat(trimmed);
      return isNaN(n) ? null : ({ number_value: n } as CustomFieldValue);
    }
    case 'CUSTOM_FIELD_TYPE_DATE':
    case 'CUSTOM_FIELD_TYPE_DATETIME': {
      const d = new Date(trimmed);
      return isNaN(d.getTime()) ? null : ({ date_value: d.toISOString() } as unknown as CustomFieldValue);
    }
    case 'CUSTOM_FIELD_TYPE_SELECT':
      return { select_value: trimmed } as CustomFieldValue;
    case 'CUSTOM_FIELD_TYPE_CHECKBOX':
      return { checkbox_value: trimmed === 'true' } as CustomFieldValue;
    case 'CUSTOM_FIELD_TYPE_URL':
      return { url_value: trimmed } as CustomFieldValue;
    default:
      return { text_value: trimmed } as CustomFieldValue;
  }
}

// ---------------------------------------------------------------------------
// Board column builder (supports custom status definitions)
// ---------------------------------------------------------------------------

/** Default board statuses used when no custom definitions exist. */
export const DEFAULT_BOARD_STATUSES = [
  'TASK_STATUS_OPEN',
  'TASK_STATUS_IN_PROGRESS',
  'TASK_STATUS_IN_REVIEW',
  'TASK_STATUS_BLOCKED',
  'TASK_STATUS_DONE',
] as const;

/** Default accent colors for base statuses (Tailwind classes). */
export const DEFAULT_STATUS_ACCENT: Record<string, string> = {
  TASK_STATUS_OPEN: 'bg-blue-500',
  TASK_STATUS_IN_PROGRESS: 'bg-yellow-500',
  TASK_STATUS_IN_REVIEW: 'bg-orange-500',
  TASK_STATUS_BLOCKED: 'bg-red-500',
  TASK_STATUS_DONE: 'bg-green-500',
};

export interface BoardColumn {
  id: string;
  label: string;
  labelKey: string;
  accentColor: string;
  accentHex: string;
  tasks: Task[];
}

/**
 * Build board columns from custom status definitions or fall back to defaults.
 * When custom definitions exist, tasks are matched by `custom_status` slug.
 * When using defaults, tasks are matched by base `status` enum value.
 */
export function buildBoardColumns(
  definitions: TaskStatusDefinition[],
  tasks: Task[],
): BoardColumn[] {
  if (definitions.length === 0) {
    return buildDefaultBoardColumns(tasks);
  }

  const sorted = [...definitions].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const columns: BoardColumn[] = sorted.map(def => ({
    id: def.slug,
    label: def.display_name,
    labelKey: '',
    accentColor: '',
    accentHex: def.color || '#6b7280',
    tasks: [],
  }));

  const columnBySlug = new Map<string, BoardColumn>();
  const columnByCategory = new Map<string, BoardColumn>();
  for (const col of columns) {
    columnBySlug.set(col.id, col);
  }
  for (const def of sorted) {
    const cat = typeof def.category === 'string' ? def.category : '';
    if (cat && !columnByCategory.has(cat)) {
      const col = columnBySlug.get(def.slug);
      if (col) columnByCategory.set(cat, col);
    }
  }

  for (const task of tasks) {
    const customStatus = getTaskCustomStatus(task);
    const baseStatus = getTaskStatus(task);

    const target =
      (customStatus && columnBySlug.get(customStatus)) ||
      (baseStatus && columnByCategory.get(baseStatus)) ||
      columns[0];

    if (target) {
      target.tasks.push(task);
    }
  }

  return columns;
}

function buildDefaultBoardColumns(tasks: Task[]): BoardColumn[] {
  const columns: BoardColumn[] = DEFAULT_BOARD_STATUSES.map(status => {
    const info = taskStatusMap[status] ?? { labelKey: 'common.unknown' };
    return {
      id: status,
      label: '',
      labelKey: info.labelKey,
      accentColor: DEFAULT_STATUS_ACCENT[status] || 'bg-muted',
      accentHex: '',
      tasks: [],
    };
  });

  const columnMap = new Map<string, BoardColumn>();
  for (const col of columns) {
    columnMap.set(col.id, col);
  }

  for (const task of tasks) {
    const status = getTaskStatus(task) || 'TASK_STATUS_OPEN';
    const col = columnMap.get(status) || columns[0];
    col.tasks.push(task);
  }

  return columns;
}

/** Base status category options for creating custom status definitions. */
export const baseCategoryOptions: { value: string; labelKey: string }[] = [
  { value: 'TASK_STATUS_OPEN', labelKey: 'task.status.open' },
  { value: 'TASK_STATUS_IN_PROGRESS', labelKey: 'task.status.inProgress' },
  { value: 'TASK_STATUS_IN_REVIEW', labelKey: 'task.status.inReview' },
  { value: 'TASK_STATUS_BLOCKED', labelKey: 'task.status.blocked' },
  { value: 'TASK_STATUS_DONE', labelKey: 'task.status.done' },
  { value: 'TASK_STATUS_CANCELLED', labelKey: 'task.status.cancelled' },
];

/** Validate a status definition form, returns i18n error keys. */
export function validateStatusDefinition(data: {
  slug: string;
  display_name: string;
  category: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.slug.trim()) errors.slug = 'validation.required';
  if (!data.display_name.trim()) errors.display_name = 'validation.required';
  if (!data.category) errors.category = 'validation.required';
  if (data.slug && !/^[a-z][a-z0-9_]*$/.test(data.slug)) {
    errors.slug = 'task.statusDef.slugFormat';
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Priority icon helpers
// ---------------------------------------------------------------------------

export function getPriorityIconPath(priority: string): string {
  switch (priority) {
    case 'TASK_PRIORITY_HIGHEST':
      return 'M5 11l7-7 7 7M5 19l7-7 7 7'; // double chevron up
    case 'TASK_PRIORITY_HIGH':
      return 'M5 15l7-7 7 7'; // chevron up
    case 'TASK_PRIORITY_MEDIUM':
      return 'M20 12H4'; // dash
    case 'TASK_PRIORITY_LOW':
      return 'M19 9l-7 7-7-7'; // chevron down
    case 'TASK_PRIORITY_LOWEST':
      return 'M19 13l-7 7-7-7M19 5l-7 7-7-7'; // double chevron down
    default:
      return '';
  }
}

export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'TASK_PRIORITY_HIGHEST':
    case 'TASK_PRIORITY_HIGH':
      return 'text-destructive';
    case 'TASK_PRIORITY_MEDIUM':
      return 'text-yellow-500';
    case 'TASK_PRIORITY_LOW':
    case 'TASK_PRIORITY_LOWEST':
      return 'text-primary';
    default:
      return 'text-muted-foreground';
  }
}

// ---------------------------------------------------------------------------
// Due date helpers
// ---------------------------------------------------------------------------

export function isDueOverdue(dueDate: string | undefined | null): boolean {
  if (!dueDate) return false;
  try {
    return new Date(dueDate).getTime() < Date.now();
  } catch {
    return false;
  }
}

export function isDueSoon(dueDate: string | undefined | null, daysThreshold = 3): boolean {
  if (!dueDate) return false;
  try {
    const due = new Date(dueDate).getTime();
    const now = Date.now();
    return due > now && due - now < daysThreshold * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function getDueDateUrgency(
  dueDate: string | undefined | null,
  status: string,
): 'overdue' | 'soon' | 'normal' | 'none' {
  if (!dueDate) return 'none';
  if (status === 'TASK_STATUS_DONE' || status === 'TASK_STATUS_CANCELLED' || status === 'TASK_STATUS_ARCHIVED') {
    return 'normal';
  }
  if (isDueOverdue(dueDate)) return 'overdue';
  if (isDueSoon(dueDate)) return 'soon';
  return 'normal';
}

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

export type TaskFilter = 'all' | 'open' | 'in_progress' | 'in_review' | 'blocked' | 'done';

export const taskFilterToStatus: Record<TaskFilter, string | null> = {
  all: null,
  open: 'TASK_STATUS_OPEN',
  in_progress: 'TASK_STATUS_IN_PROGRESS',
  in_review: 'TASK_STATUS_IN_REVIEW',
  blocked: 'TASK_STATUS_BLOCKED',
  done: 'TASK_STATUS_DONE',
};

export const taskFilters: { key: TaskFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'common.all' },
  { key: 'open', labelKey: 'task.status.open' },
  { key: 'in_progress', labelKey: 'task.status.inProgress' },
  { key: 'in_review', labelKey: 'task.status.inReview' },
  { key: 'blocked', labelKey: 'task.status.blocked' },
  { key: 'done', labelKey: 'task.status.done' },
];

export const priorityFilterOptions: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'task.allPriorities' },
  { value: 'TASK_PRIORITY_HIGHEST', labelKey: 'task.priority.highest' },
  { value: 'TASK_PRIORITY_HIGH', labelKey: 'task.priority.high' },
  { value: 'TASK_PRIORITY_MEDIUM', labelKey: 'task.priority.medium' },
  { value: 'TASK_PRIORITY_LOW', labelKey: 'task.priority.low' },
  { value: 'TASK_PRIORITY_LOWEST', labelKey: 'task.priority.lowest' },
];

export function filterTasks(
  tasks: Task[],
  search: string,
  statusFilter: TaskFilter,
  priorityFilter?: string,
): Task[] {
  let filtered = tasks;

  const mappedStatus = taskFilterToStatus[statusFilter];
  if (mappedStatus) {
    filtered = filtered.filter((t: any) => getTaskStatus(t) === mappedStatus);
  }

  if (priorityFilter) {
    filtered = filtered.filter((t: any) => getTaskPriority(t) === priorityFilter);
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter((t: any) =>
      t.title?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q),
    );
  }

  return filtered;
}

export function sortTasksByActivity(tasks: Task[]): Task[] {
  return [...tasks].sort((a: any, b: any) => {
    const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
    const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
    return bTime - aTime;
  });
}

export function sortTasksByPriority(tasks: Task[]): Task[] {
  const order: Record<string, number> = {
    TASK_PRIORITY_HIGHEST: 0,
    TASK_PRIORITY_HIGH: 1,
    TASK_PRIORITY_MEDIUM: 2,
    TASK_PRIORITY_LOW: 3,
    TASK_PRIORITY_LOWEST: 4,
  };
  return [...tasks].sort((a: any, b: any) => {
    const ap = order[getTaskPriority(a)] ?? 5;
    const bp = order[getTaskPriority(b)] ?? 5;
    return ap - bp;
  });
}

// ---------------------------------------------------------------------------
// Permission checks
// ---------------------------------------------------------------------------

export function canEditTask(item: { status?: string; task?: { status?: string } }): boolean {
  const status = (item as any)?.task?.status || item.status;
  return !(
    status === 'TASK_STATUS_ARCHIVED' ||
    status === 'TASK_STATUS_CANCELLED' ||
    status === 'archived' ||
    status === 'deleted'
  );
}

export function canDeleteTask(item: { status?: string }): boolean {
  return item.status !== 'deleted';
}

// ---------------------------------------------------------------------------
// Validation (returns i18n key strings)
// ---------------------------------------------------------------------------

export function validateTaskForm(data: {
  title: string;
  description?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.title.trim()) {
    errors.title = 'validation.required';
  } else if (data.title.trim().length < 2) {
    errors.title = 'validation.minLength';
  } else if (data.title.trim().length > 200) {
    errors.title = 'validation.maxLength';
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Status transition options
// ---------------------------------------------------------------------------

export const statusOptions: { value: string; labelKey: string }[] = [
  { value: 'TASK_STATUS_DRAFT', labelKey: 'task.status.draft' },
  { value: 'TASK_STATUS_OPEN', labelKey: 'task.status.open' },
  { value: 'TASK_STATUS_IN_PROGRESS', labelKey: 'task.status.inProgress' },
  { value: 'TASK_STATUS_IN_REVIEW', labelKey: 'task.status.inReview' },
  { value: 'TASK_STATUS_BLOCKED', labelKey: 'task.status.blocked' },
  { value: 'TASK_STATUS_DONE', labelKey: 'task.status.done' },
  { value: 'TASK_STATUS_CANCELLED', labelKey: 'task.status.cancelled' },
  { value: 'TASK_STATUS_ARCHIVED', labelKey: 'task.status.archived' },
];

// ---------------------------------------------------------------------------
// Checklist progress
// ---------------------------------------------------------------------------

export function getChecklistProgress(item: any): { done: number; total: number } | null {
  const total = item?.task?.checklist_total;
  const done = item?.task?.checklist_done;
  if (total == null || total === 0) return null;
  return { done: done ?? 0, total };
}

// ---------------------------------------------------------------------------
// Select options (for create/edit forms)
// ---------------------------------------------------------------------------

export const taskTypeOptions: { value: string; labelKey: string }[] = Object.entries(taskTypeMap).map(
  ([value, info]) => ({ value, labelKey: info.labelKey }),
);

export const taskPriorityOptions: { value: string; labelKey: string }[] = [
  { value: 'TASK_PRIORITY_LOWEST', labelKey: 'task.priority.lowest' },
  { value: 'TASK_PRIORITY_LOW', labelKey: 'task.priority.low' },
  { value: 'TASK_PRIORITY_MEDIUM', labelKey: 'task.priority.medium' },
  { value: 'TASK_PRIORITY_HIGH', labelKey: 'task.priority.high' },
  { value: 'TASK_PRIORITY_HIGHEST', labelKey: 'task.priority.highest' },
];

// ---------------------------------------------------------------------------
// Task type quick-templates (default priority per type)
// ---------------------------------------------------------------------------

export interface TaskTypeTemplate {
  type: string;
  labelKey: string;
  iconPath: string;
  defaultPriority: string;
}

export const taskTypeTemplates: TaskTypeTemplate[] = [
  { type: 'TASK_TYPE_TASK', labelKey: 'task.type.task', iconPath: taskTypeMap.TASK_TYPE_TASK.iconPath, defaultPriority: 'TASK_PRIORITY_MEDIUM' },
  { type: 'TASK_TYPE_BUG', labelKey: 'task.type.bug', iconPath: taskTypeMap.TASK_TYPE_BUG.iconPath, defaultPriority: 'TASK_PRIORITY_HIGH' },
  { type: 'TASK_TYPE_FEATURE', labelKey: 'task.type.feature', iconPath: taskTypeMap.TASK_TYPE_FEATURE.iconPath, defaultPriority: 'TASK_PRIORITY_MEDIUM' },
  { type: 'TASK_TYPE_IMPROVEMENT', labelKey: 'task.type.improvement', iconPath: taskTypeMap.TASK_TYPE_IMPROVEMENT.iconPath, defaultPriority: 'TASK_PRIORITY_MEDIUM' },
  { type: 'TASK_TYPE_SUPPORT', labelKey: 'task.type.support', iconPath: taskTypeMap.TASK_TYPE_SUPPORT.iconPath, defaultPriority: 'TASK_PRIORITY_HIGH' },
  { type: 'TASK_TYPE_QUESTION', labelKey: 'task.type.question', iconPath: taskTypeMap.TASK_TYPE_QUESTION.iconPath, defaultPriority: 'TASK_PRIORITY_LOW' },
];

// ---------------------------------------------------------------------------
// Enum label formatting
// ---------------------------------------------------------------------------

export function formatEnumLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

export function formatActivityType(type: string): string {
  return formatEnumLabel(type, 'TASK_ACTIVITY_TYPE_');
}

// ---------------------------------------------------------------------------
// Markdown description helpers (for preview)
// ---------------------------------------------------------------------------

export function parseTags(tagsStr: string): string[] {
  if (!tagsStr.trim()) return [];
  return tagsStr.split(',').map(t => t.trim()).filter(Boolean);
}

export function isDescriptionEmpty(description: string): boolean {
  return !description.trim();
}

// ---------------------------------------------------------------------------
// Build create payload
// ---------------------------------------------------------------------------

export function buildCreatePayload(formData: {
  title: string;
  description: string;
  type: string;
  priority: string;
  assignee: string;
  assignee_name?: string;
  due_date: string;
  parent_id?: string;
  parent_type?: string;
}) {
  const payload: any = {
    title: formData.title.trim(),
    description: formData.description.trim(),
    type: formData.type,
    priority: formData.priority,
  };

  if (formData.assignee) {
    payload.assignee = { entity: { id: formData.assignee }, display_name: formData.assignee_name || '' };
  }

  if (formData.due_date) {
    payload.due_date = new Date(formData.due_date).toISOString();
  }

  if (formData.parent_id) {
    payload.parent = { id: formData.parent_id, type_name: formData.parent_type || '' };
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Tag assignment helpers (TagService integration)
// ---------------------------------------------------------------------------

import { tagApi } from '@core/api';
import type { Tag } from '@core/types/tag';

/**
 * Assign tags to an entity via TagService.
 * Called after entity creation. Returns the count of successfully assigned tags.
 */
export async function assignTagsToEntity(
  tags: Tag[],
  entityId: string,
  entityType?: string,
): Promise<number> {
  let assigned = 0;
  for (const tag of tags) {
    const tagId = tag.entity?.id;
    if (!tagId) continue;
    try {
      await tagApi.TagEntity(tagId, {
        tag_id: tagId,
        target: { id: entityId, type_id: entityType || '' } as any,
      });
      assigned++;
    } catch {
      // Non-critical — tag assignment failure shouldn't block task creation
    }
  }
  return assigned;
}

/**
 * Sync tags for an existing entity via TagService.
 * Compares current vs desired tags, adds new and removes stale.
 */
export async function syncTagsForEntity(
  currentTags: Tag[],
  newTags: Tag[],
  entityId: string,
  entityType?: string,
): Promise<void> {
  const currentIds = new Set(currentTags.map(t => t.entity?.id).filter(Boolean));
  const newIds = new Set(newTags.map(t => t.entity?.id).filter(Boolean));

  // Tags to add
  const toAdd = newTags.filter(t => t.entity?.id && !currentIds.has(t.entity.id));
  // Tags to remove
  const toRemove = currentTags.filter(t => t.entity?.id && !newIds.has(t.entity.id));

  await Promise.allSettled([
    ...toAdd.map(tag => {
      const tagId = tag.entity?.id || '';
      return tagApi.TagEntity(tagId, {
        tag_id: tagId,
        target: { id: entityId, type_id: entityType || '' } as any,
      });
    }),
    ...toRemove.map(tag => {
      const tagId = tag.entity?.id || '';
      return tagApi.UntagEntity(tagId, entityType || '', entityId);
    }),
  ]);
}

/**
 * Load existing tags for an entity from TagService.
 */
export async function loadEntityTags(
  entityId: string,
  entityType?: string,
): Promise<Tag[]> {
  try {
    const result = await tagApi.ListEntityTags(entityType || '', entityId);
    return result?.tags?.tags ?? [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Bulk operations
// ---------------------------------------------------------------------------

import { taskApi } from '@core/api';

export interface BulkResult {
  success: number;
  failed: number;
}

// ---------------------------------------------------------------------------
// Request builders (shared by inline edit, quick-add and bulk actions)
// ---------------------------------------------------------------------------

/** Task fields that can be patched through UpdateTask. */
export type EditableTaskField =
  | 'title'
  | 'description'
  | 'type'
  | 'priority'
  | 'due_date'
  | 'start_date'
  | 'tags'
  | 'progress_percent';

/**
 * Field-mask paths accepted by the task service `UpdateTask` handler.
 * Top-level entity fields are bare; aspect fields are prefixed with `task.`.
 */
const FIELD_MASK_PATHS: Record<EditableTaskField, string> = {
  title: 'title',
  description: 'description',
  type: 'type',
  priority: 'task.priority',
  due_date: 'task.due_date',
  start_date: 'task.start_date',
  tags: 'task.tags',
  progress_percent: 'task.progress_percent',
};

/**
 * Build a field-masked `UpdateTaskRequest` for one task.
 * Only the supplied fields are sent, so an inline edit can never clobber
 * a value the user did not touch.
 */
export function buildTaskFieldUpdate(
  taskId: string,
  fields: Partial<Pick<Task, EditableTaskField>>,
): UpdateTaskRequest {
  const paths: string[] = [];
  for (const key of Object.keys(fields) as EditableTaskField[]) {
    const path = FIELD_MASK_PATHS[key];
    if (path) paths.push(path);
  }

  return {
    task: { entity: { id: taskId }, ...fields } as Task,
    field_mask: paths,
  };
}

/**
 * Build a `ChangeTaskStatusRequest` for a board column / status picker choice.
 *
 * A column may be either a base enum status (`TASK_STATUS_IN_REVIEW`) or the
 * slug of a custom `TaskStatusDefinition`. The service stores the enum in
 * `status` and the slug in `custom_status`, so a custom column has to send
 * both: the slug plus the definition's `category` as the enum.
 */
export function buildStatusChangeRequest(
  taskId: string,
  columnId: string,
  definitions: TaskStatusDefinition[] = [],
): ChangeTaskStatusRequest {
  const definition = definitions.find((def) => def.slug === columnId);
  // The handler takes the task id from the path; only `id` is meaningful here.
  const task = { id: taskId } as Entity;

  if (definition) {
    return {
      task,
      status: (definition.category || 'TASK_STATUS_OPEN') as Task['status'],
      custom_status: definition.slug,
    };
  }

  return {
    task,
    status: columnId as Task['status'],
    custom_status: '',
  };
}

export async function bulkUpdateStatus(
  taskIds: string[],
  columnId: string,
  definitions: TaskStatusDefinition[] = [],
): Promise<BulkResult> {
  let success = 0;
  let failed = 0;
  await Promise.allSettled(
    taskIds.map(async (id) => {
      try {
        await taskApi.ChangeTaskStatus(id, buildStatusChangeRequest(id, columnId, definitions));
        success++;
      } catch {
        failed++;
      }
    }),
  );
  return { success, failed };
}

export async function bulkUpdatePriority(
  taskIds: string[],
  priority: string,
): Promise<BulkResult> {
  let success = 0;
  let failed = 0;
  await Promise.allSettled(
    taskIds.map(async (id) => {
      try {
        await taskApi.UpdateTask(id, buildTaskFieldUpdate(id, { priority: priority as Task['priority'] }));
        success++;
      } catch {
        failed++;
      }
    }),
  );
  return { success, failed };
}

/** Assign every task to `assigneeId`; an empty id clears the assignee. */
export async function bulkAssign(
  taskIds: string[],
  assigneeId: string,
): Promise<BulkResult> {
  let success = 0;
  let failed = 0;
  await Promise.allSettled(
    taskIds.map(async (id) => {
      try {
        await taskApi.AssignTask(id, {
          task: { id },
          assignee: assigneeId ? { entity: { id: assigneeId } } : undefined,
        } as any);
        success++;
      } catch {
        failed++;
      }
    }),
  );
  return { success, failed };
}

export async function bulkDelete(
  taskIds: string[],
): Promise<BulkResult> {
  let success = 0;
  let failed = 0;
  await Promise.allSettled(
    taskIds.map(async (id) => {
      try {
        await taskApi.DeleteTask(id);
        success++;
      } catch {
        failed++;
      }
    }),
  );
  return { success, failed };
}

// ---------------------------------------------------------------------------
// Client-side refinement of a TaskViewFilter
// ---------------------------------------------------------------------------

/**
 * Test one task against a structured `TaskViewFilter` (AND semantics, matching
 * the server default of `match_any = false`).
 *
 * Filtering is the server's job — the list endpoints apply the same filter in
 * SQL. This exists for callers that hold a list the server did not filter:
 * optimistically inserted rows, and views that slice an already-fetched page.
 */
export function matchesTaskViewFilter(task: Task, filter?: TaskViewFilter): boolean {
  if (!filter) return true;

  const statuses = filter.statuses?.statuses ?? [];
  if (statuses.length && !statuses.includes(getTaskStatus(task) as never)) {
    return false;
  }

  const priorities = filter.priorities?.priorities ?? [];
  if (priorities.length && !priorities.includes(getTaskPriority(task) as never)) {
    return false;
  }

  const types = filter.types?.types ?? [];
  if (types.length && !types.includes(getTaskType(task) as never)) {
    return false;
  }

  if (filter.assignee_ids?.length) {
    const assignee = getTaskAssignee(task);
    const assigneeId = assignee?.entity?.id || assignee?.id || '';
    if (!assigneeId || !filter.assignee_ids.includes(assigneeId)) {
      return false;
    }
  }

  if (filter.tags?.length) {
    const tags = getTaskTags(task);
    if (!filter.tags.some((tag) => tags.includes(tag))) {
      return false;
    }
  }

  if (filter.due_after || filter.due_before) {
    const dueDate = getTaskDueDate(task);
    if (!dueDate) return false;
    const due = new Date(dueDate).getTime();
    if (Number.isNaN(due)) return false;
    if (filter.due_after && due < new Date(filter.due_after).getTime()) return false;
    if (filter.due_before && due > new Date(filter.due_before).getTime()) return false;
  }

  const query = filter.search_query?.trim().toLowerCase();
  if (query) {
    const haystack = [
      (task as any).title,
      (task as any).description,
      ...getTaskTags(task),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(query)) return false;
  }

  return true;
}

/** Narrow a list of tasks with `matchesTaskViewFilter`. */
export function applyTaskViewFilter(tasks: Task[], filter?: TaskViewFilter): Task[] {
  if (!filter) return tasks;
  return tasks.filter((task) => matchesTaskViewFilter(task, filter));
}
